'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type {
    Blog,
    BlogSlim,
    BlogAdmin,
    BlogCreate,
    BlogUpdate,
    BlogAdminStatusUpdate,
    KnowledgeHub,
    KnowledgeHubSlim,
    KnowledgeHubAdmin,
    KnowledgeHubCreate,
    KnowledgeHubUpdate,
    KnowledgeHubAdminStatusUpdate,
    TeamMember,
    TeamMemberSlim,
    TeamMemberAdmin,
    TeamMemberCreate,
    TeamMemberUpdate,
    TeamMemberAdminStatusUpdate,
    User,
    StaffAccountCreate,
    UserUpdateAdmin,
    LoginCredentials,
    AuthTokens,
    PasswordChange,
    UserProfileUpdate,
    PaginatedResponse,
    AdminPageResponse,
    NewsletterSubscriptionAdmin,
    NewsletterCreate,
    NewsletterRead,
    NewsletterListItem,
    ContactSubmission,
    ContactSubmissionCreate,
    DashboardStats,
    KnowledgeHubCategory,
    TeamRole,
} from '@/types';

// ============= BLOGS =============

export const useBlogs = (page = 1, pageSize = 20, search?: string) => {
    return useQuery({
        queryKey: ['blogs', page, pageSize, search],
        queryFn: async () => {
            const params: any = { page, page_size: pageSize };
            if (search) params.search = search;
            const { data } = await api.get<PaginatedResponse<BlogSlim>>('/blogs', { params });
            return data;
        },
    });
};

export const useBlog = (id: string) => {
    return useQuery({
        queryKey: ['blog', id],
        queryFn: async () => {
            const { data } = await api.get<Blog>(`/blogs/${id}`);
            return data;
        },
        enabled: !!id,
    });
};

export const useBlogsAdmin = (page = 1, pageSize = 20, filters?: any) => {
    return useQuery({
        queryKey: ['blogs-admin', page, pageSize, filters],
        queryFn: async () => {
            const params = { page, page_size: pageSize, ...filters };
            const { data } = await api.get<PaginatedResponse<BlogAdmin>>('/blogs/admin', { params });
            return data;
        },
    });
};

export const useCreateBlog = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (blog: BlogCreate) => {
            const { data } = await api.post<Blog>('/blogs', blog);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useUpdateBlog = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, blog }: { id: string; blog: BlogUpdate }) => {
            const { data } = await api.put<Blog>(`/blogs/${id}`, blog);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useDeleteBlog = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/blogs/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useUploadBlogImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, image }: { id: string; image: File }) => {
            const formData = new FormData();
            formData.append('image', image);
            const { data } = await api.post<Blog>(`/blogs/${id}/image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useReplaceBlogImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, image }: { id: string; image: File }) => {
            const formData = new FormData();
            formData.append('image', image);
            const { data } = await api.put<Blog>(`/blogs/${id}/image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useDeleteBlogImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await api.delete<Blog>(`/blogs/${id}/image`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useUploadBlogAuthorImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, image }: { id: string; image: File }) => {
            const formData = new FormData();
            formData.append('author_image', image);
            const { data } = await api.post<Blog>(`/blogs/${id}/author-image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useReplaceBlogAuthorImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, image }: { id: string; image: File }) => {
            const formData = new FormData();
            formData.append('author_image', image);
            const { data } = await api.put<Blog>(`/blogs/${id}/author-image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useDeleteBlogAuthorImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await api.delete<Blog>(`/blogs/${id}/author-image`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs'] });
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

export const useUpdateBlogStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            status,
        }: {
            id: string;
            status: BlogAdminStatusUpdate;
        }) => {
            const { data } = await api.patch<BlogAdmin>(`/blogs/admin/${id}/status`, status);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogs-admin'] });
        },
    });
};

// ============= KNOWLEDGE HUB =============

export const useKnowledgeHub = (
    page = 1,
    pageSize = 20,
    search?: string,
    category?: KnowledgeHubCategory
) => {
    return useQuery({
        queryKey: ['knowledge-hub', page, pageSize, search, category],
        queryFn: async () => {
            const params: any = { page, page_size: pageSize };
            if (search) params.search = search;
            if (category) params.category = category;
            const { data } = await api.get<PaginatedResponse<KnowledgeHubSlim>>('/knowledge-hub', {
                params,
            });
            return data;
        },
    });
};

export const useKnowledgeHubArticle = (id: string) => {
    return useQuery({
        queryKey: ['knowledge-hub-article', id],
        queryFn: async () => {
            const { data } = await api.get<KnowledgeHub>(`/knowledge-hub/${id}`);
            return data;
        },
        enabled: !!id,
    });
};

export const useKnowledgeHubAdmin = (page = 1, pageSize = 20, filters?: any) => {
    return useQuery({
        queryKey: ['knowledge-hub-admin', page, pageSize, filters],
        queryFn: async () => {
            const params = { page, page_size: pageSize, ...filters };
            const { data } = await api.get<PaginatedResponse<KnowledgeHubAdmin>>('/knowledge-hub/admin', {
                params,
            });
            return data;
        },
    });
};

export const useKnowledgeHubAdminItem = (id: string) => {
    return useQuery({
        queryKey: ['knowledge-hub-admin-item', id],
        queryFn: async () => {
            const { data } = await api.get<KnowledgeHubAdmin>(`/knowledge-hub/admin/${id}`);
            return data;
        },
        enabled: !!id,
    });
};

export const useCreateKnowledgeHub = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (article: KnowledgeHubCreate) => {
            const { data } = await api.post<KnowledgeHub>('/knowledge-hub', article);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub'] });
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub-admin'] });
        },
    });
};

export const useUpdateKnowledgeHub = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, article }: { id: string; article: KnowledgeHubUpdate }) => {
            const { data } = await api.put<KnowledgeHub>(`/knowledge-hub/${id}`, article);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub'] });
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub-admin'] });
        },
    });
};

export const useDeleteKnowledgeHub = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/knowledge-hub/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub'] });
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub-admin'] });
        },
    });
};

export const useUpdateKnowledgeHubStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            status,
        }: {
            id: string;
            status: KnowledgeHubAdminStatusUpdate;
        }) => {
            const { data } = await api.patch<KnowledgeHubAdmin>(`/knowledge-hub/admin/${id}/status`, status);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub-admin'] });
        },
    });
};

export const useUploadKnowledgeHubImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, image }: { id: string; image: File }) => {
            const formData = new FormData();
            formData.append('image', image);
            const { data } = await api.post<KnowledgeHub>(`/knowledge-hub/${id}/image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub'] });
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub-admin'] });
        },
    });
};

export const useReplaceKnowledgeHubImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, image }: { id: string; image: File }) => {
            const formData = new FormData();
            formData.append('image', image);
            const { data } = await api.put<KnowledgeHub>(`/knowledge-hub/${id}/image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub'] });
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub-admin'] });
        },
    });
};

export const useDeleteKnowledgeHubImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await api.delete<KnowledgeHub>(`/knowledge-hub/${id}/image`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub'] });
            queryClient.invalidateQueries({ queryKey: ['knowledge-hub-admin'] });
        },
    });
};

// ============= TEAM =============

export const useTeam = (page = 1, pageSize = 20, role?: TeamRole) => {
    return useQuery({
        queryKey: ['team', page, pageSize, role],
        queryFn: async () => {
            const params: any = { page, page_size: pageSize };
            if (role) params.role = role;
            const { data } = await api.get<PaginatedResponse<TeamMemberSlim>>('/team', { params });
            return data;
        },
    });
};

export const useTeamMember = (id: string) => {
    return useQuery({
        queryKey: ['team-member', id],
        queryFn: async () => {
            const { data } = await api.get<TeamMember>(`/team/${id}`);
            return data;
        },
        enabled: !!id,
    });
};

export const useTeamAdmin = (page = 1, pageSize = 20, filters?: any) => {
    return useQuery({
        queryKey: ['team-admin', page, pageSize, filters],
        queryFn: async () => {
            const params = { page, page_size: pageSize, ...filters };
            const { data } = await api.get<PaginatedResponse<TeamMemberAdmin>>('/team/admin', { params });
            return data;
        },
    });
};

export const useCreateTeamMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (member: TeamMemberCreate) => {
            const { data } = await api.post<TeamMember>('/team', member);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            queryClient.invalidateQueries({ queryKey: ['team-admin'] });
        },
    });
};

export const useUpdateTeamMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, member }: { id: string; member: TeamMemberUpdate }) => {
            const { data } = await api.put<TeamMember>(`/team/${id}`, member);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            queryClient.invalidateQueries({ queryKey: ['team-admin'] });
        },
    });
};

export const useDeleteTeamMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/team/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            queryClient.invalidateQueries({ queryKey: ['team-admin'] });
        },
    });
};

export const useUpdateTeamMemberStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            status,
        }: {
            id: string;
            status: TeamMemberAdminStatusUpdate;
        }) => {
            const { data } = await api.patch<TeamMemberAdmin>(`/team/admin/${id}/status`, status);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team-admin'] });
        },
    });
};

export const useUploadTeamMemberImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, image }: { id: string; image: File }) => {
            const formData = new FormData();
            formData.append('image', image);
            const { data } = await api.post<TeamMember>(`/team/${id}/image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            queryClient.invalidateQueries({ queryKey: ['team-admin'] });
        },
    });
};

export const useReplaceTeamMemberImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, image }: { id: string; image: File }) => {
            const formData = new FormData();
            formData.append('image', image);
            const { data } = await api.put<TeamMember>(`/team/${id}/image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            queryClient.invalidateQueries({ queryKey: ['team-admin'] });
        },
    });
};

export const useDeleteTeamMemberImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await api.delete<TeamMember>(`/team/${id}/image`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            queryClient.invalidateQueries({ queryKey: ['team-admin'] });
        },
    });
};

// ============= NEWSLETTER =============

export const useNewsletterSubscriptions = (page = 1, pageSize = 20, filters?: any) => {
    return useQuery({
        queryKey: ['newsletter-subscriptions', page, pageSize, filters],
        queryFn: async () => {
            const params = { page, page_size: pageSize, ...filters };
            const { data } = await api.get<PaginatedResponse<NewsletterSubscriptionAdmin>>(
                '/newsletter/admin/subscriptions',
                { params }
            );
            return data;
        },
    });
};

export const useCreateNewsletter = () => {
    return useMutation({
        mutationFn: async (payload: NewsletterCreate) => {
            const { data } = await api.post<NewsletterRead>('/newsletter/admin/newsletters', payload);
            return data;
        },
    });
};

export const useNewsletterList = (page = 1, pageSize = 20, filters?: any) => {
    return useQuery({
        queryKey: ['newsletter-list', page, pageSize, filters],
        queryFn: async () => {
            const params = { page, page_size: pageSize, ...filters };
            const { data } = await api.get<PaginatedResponse<NewsletterListItem>>(
                '/newsletter/admin/newsletters',
                { params }
            );
            return data;
        },
    });
};

export const useDeleteNewsletter = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/newsletter/admin/newsletters/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['newsletter-list'] });
        },
    });
};

export const useUpdateSubscriptionStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            status,
        }: {
            id: string;
            status: { is_deleted?: boolean; is_active?: boolean };
        }) => {
            const { data } = await api.patch<NewsletterSubscriptionAdmin>(
                `/newsletter/admin/subscriptions/${id}/status`,
                status
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['newsletter-subscriptions'] });
        },
    });
};

// ============= CONTACT US =============

export const useContactSubmissions = (page = 1, pageSize = 20, filters?: any) => {
    return useQuery({
        queryKey: ['contact-submissions', page, pageSize, filters],
        queryFn: async () => {
            const params = { page, page_size: pageSize, ...filters };
            const { data } = await api.get<PaginatedResponse<ContactSubmission>>(
                '/contact-us/submissions',
                { params }
            );
            return data;
        },
    });
};

export const useContactSubmission = (id: string) => {
    return useQuery({
        queryKey: ['contact-submission', id],
        queryFn: async () => {
            const { data } = await api.get<ContactSubmission>(`/contact-us/submissions/${id}`);
            return data;
        },
        enabled: !!id,
    });
};

export const useCreateContactSubmission = () => {
    return useMutation({
        mutationFn: async (payload: ContactSubmissionCreate) => {
            const { data } = await api.post<ContactSubmission>('/contact-us/submissions', payload);
            return data;
        },
    });
};

export const useMarkContactRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await api.patch<ContactSubmission>(`/contact-us/submissions/${id}/read`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact-submissions'] });
            queryClient.invalidateQueries({ queryKey: ['contact-submission'] });
        },
    });
};

export const useMarkContactResolved = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await api.patch<ContactSubmission>(
                `/contact-us/submissions/${id}/resolved`
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact-submissions'] });
            queryClient.invalidateQueries({ queryKey: ['contact-submission'] });
        },
    });
};

// ============= DASHBOARD STATS =============

export const useDashboardStats = () => {
    return useQuery({
        queryKey: ['dashboard-stats'],
        queryFn: async () => {
            const { data } = await api.get<DashboardStats>('/admin/stats');
            return data;
        },
    });
};

// ============= AUTH =============

export const useLogin = () => {
    return useMutation({
        mutationFn: async (credentials: LoginCredentials) => {
            const formData = new FormData();
            formData.append('username', credentials.username);
            formData.append('password', credentials.password);
            const { data } = await api.post<AuthTokens>('/login', formData, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
            return data;
        },
    });
};

export const useLogout = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await api.post('/revoke');
        },
        onSuccess: () => {
            localStorage.removeItem('access_token');
            queryClient.clear();
        },
    });
};

export const useProfile = () => {
    const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('access_token');
    return useQuery({
        queryKey: ['profile'],
        queryFn: async () => {
            const { data } = await api.get<User>('/me');
            return data;
        },
        enabled: hasToken,
        retry: false,
        refetchOnMount: false,
    });
};

export const useChangePassword = () => {
    return useMutation({
        mutationFn: async (passwords: PasswordChange) => {
            await api.put('/me/change-password', passwords);
        },
    });
};

export const useUpdateProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: UserProfileUpdate) => {
            const { data } = await api.put<User>('/me/', payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
};

// ============= USERS (Admin) =============

export const useUsers = (page = 1, pageSize = 20, search?: string) => {
    return useQuery({
        queryKey: ['users', page, pageSize, search],
        queryFn: async () => {
            const { data } = await api.get<AdminPageResponse<User>>('/admin/users/staff', {
                params: { page, size: pageSize, query: search },
            });
            return data;
        },
    });
};

export const useCreateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (user: StaffAccountCreate) => {
            const { data } = await api.post<User>('/admin/users', user);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useUpdateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: UserUpdateAdmin }) => {
            const { data } = await api.put<User>(`/admin/users/${id}`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useDeactivateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.put(`/admin/users/${id}/deactivate`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useToggleBanUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.put(`/admin/users/${id}/ban`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useDeleteUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/admin/users/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
};

export const useSendRandomPassword = () => {
    return useMutation({
        mutationFn: async (id: string) => {
            await api.post(`/admin/users/${id}/send-random-password`);
        },
    });
};
